/**
 * A real spectrogram — STFT over decoded audio.
 *
 * The website's `spectrogram.js` renders a *synthetic* one for the hero: it
 * models a bark rather than analysing a file. The colour ramp is carried over
 * from it deliberately, because that ramp is the project's visual identity —
 * dark instrument panel through teal into amber — and reusing it makes a real
 * recording look like it belongs to the same instrument.
 *
 * The mapping is also the argument the whole project is making: **teal is
 * context, amber is the signal.** Everything a conventional corpus keeps is the
 * amber. Seeing a full minute rendered this way, with a small bright band in a
 * long teal field, teaches the thesis without a caption.
 */

/** Lifted from the website's spectrogram.js so the two renderings match. */
const STOPS: Array<[number, [number, number, number]]> = [
  [0.0, [14, 18, 20]],
  [0.3, [17, 45, 43]],
  [0.52, [47, 122, 114]],
  [0.74, [224, 162, 43]],
  [1.0, [246, 231, 196]],
];

function ramp(t: number): [number, number, number] {
  const clamped = t < 0 ? 0 : t > 1 ? 1 : t;
  for (let i = 1; i < STOPS.length; i++) {
    const hi = STOPS[i]!;
    if (clamped <= hi[0]) {
      const lo = STOPS[i - 1]!;
      const f = (clamped - lo[0]) / (hi[0] - lo[0]);
      return [
        lo[1][0] + (hi[1][0] - lo[1][0]) * f,
        lo[1][1] + (hi[1][1] - lo[1][1]) * f,
        lo[1][2] + (hi[1][2] - lo[1][2]) * f,
      ];
    }
  }
  return STOPS[STOPS.length - 1]![1];
}

const FFT_SIZE = 1024;
const HOP = 256;

/**
 * In-place iterative radix-2 Cooley–Tukey FFT.
 *
 * Hand-rolled rather than pulled from a library: it is forty lines, it has no
 * dependencies, and it keeps the artifact self-contained. `re` and `im` are
 * modified in place.
 */
function fft(re: Float32Array, im: Float32Array): void {
  const n = re.length;

  // Bit-reversal permutation.
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      [re[i], re[j]] = [re[j]!, re[i]!];
      [im[i], im[j]] = [im[j]!, im[i]!];
    }
  }

  for (let len = 2; len <= n; len <<= 1) {
    const angle = (-2 * Math.PI) / len;
    const wRe = Math.cos(angle);
    const wIm = Math.sin(angle);

    for (let i = 0; i < n; i += len) {
      let curRe = 1;
      let curIm = 0;

      for (let k = 0; k < len / 2; k++) {
        const aRe = re[i + k]!;
        const aIm = im[i + k]!;
        const bRe = re[i + k + len / 2]! * curRe - im[i + k + len / 2]! * curIm;
        const bIm = re[i + k + len / 2]! * curIm + im[i + k + len / 2]! * curRe;

        re[i + k] = aRe + bRe;
        im[i + k] = aIm + bIm;
        re[i + k + len / 2] = aRe - bRe;
        im[i + k + len / 2] = aIm - bIm;

        const nextRe = curRe * wRe - curIm * wIm;
        curIm = curRe * wIm + curIm * wRe;
        curRe = nextRe;
      }
    }
  }
}

export interface SpectrogramData {
  /** magnitudes[frame][bin], normalised 0..1 after log scaling. */
  magnitudes: Float32Array[];
  frameCount: number;
  binCount: number;
  sampleRateHz: number;
  durationMs: number;
}

/**
 * Computes the STFT.
 *
 * Synchronous, and fast enough for the minute-long recordings this app deals
 * with — roughly 200 frames per second of audio. If contributors start
 * uploading half-hour files this belongs in a worker.
 */
export function computeSpectrogram(
  samples: Float32Array,
  sampleRateHz: number,
  maxFrames = 1400,
): SpectrogramData {
  const binCount = FFT_SIZE / 2;

  // Skip frames when the recording is long, rather than computing thousands
  // that would collapse into the same pixel column anyway.
  const totalFrames = Math.max(1, Math.floor((samples.length - FFT_SIZE) / HOP));
  const stride = Math.max(1, Math.ceil(totalFrames / maxFrames));
  const frameCount = Math.max(1, Math.floor(totalFrames / stride));

  // Hann window: without it, every frame boundary produces spectral leakage
  // that reads as horizontal streaking across the whole image.
  const window = new Float32Array(FFT_SIZE);
  for (let i = 0; i < FFT_SIZE; i++) {
    window[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (FFT_SIZE - 1)));
  }

  const magnitudes: Float32Array[] = [];
  const re = new Float32Array(FFT_SIZE);
  const im = new Float32Array(FFT_SIZE);

  let peak = 1e-9;

  for (let frame = 0; frame < frameCount; frame++) {
    const offset = frame * stride * HOP;

    for (let i = 0; i < FFT_SIZE; i++) {
      re[i] = (samples[offset + i] ?? 0) * window[i]!;
      im[i] = 0;
    }

    fft(re, im);

    const column = new Float32Array(binCount);
    for (let bin = 0; bin < binCount; bin++) {
      const magnitude = Math.hypot(re[bin]!, im[bin]!);
      column[bin] = magnitude;
      if (magnitude > peak) peak = magnitude;
    }

    magnitudes.push(column);
  }

  // Log scaling, then normalise against the loudest bin in this recording.
  //
  // Per-recording normalisation is right for a *display*: a quiet recording
  // should still be legible. It would be wrong for analysis, where absolute
  // level matters — which is one reason this stays in the client and never
  // becomes a stored annotation.
  const floorDb = -70;
  for (const column of magnitudes) {
    for (let bin = 0; bin < column.length; bin++) {
      const db = 20 * Math.log10(Math.max(column[bin]!, 1e-9) / peak);
      column[bin] = Math.max(0, (db - floorDb) / -floorDb);
    }
  }

  return {
    magnitudes,
    frameCount: magnitudes.length,
    binCount,
    sampleRateHz,
    durationMs: Math.round((samples.length / sampleRateHz) * 1000),
  };
}

/**
 * Paints the spectrogram onto a canvas.
 *
 * `maxFrequencyHz` crops the vertical axis. Most animal vocalisation of
 * interest sits below 12 kHz, and rendering the full Nyquist range wastes most
 * of the image on an empty band — which makes the part that matters smaller
 * and harder to read.
 */
export function paintSpectrogram(
  canvas: HTMLCanvasElement,
  data: SpectrogramData,
  maxFrequencyHz = 12000,
): void {
  const context = canvas.getContext('2d');
  if (!context) return;

  const nyquist = data.sampleRateHz / 2;
  const usableBins = Math.max(
    1,
    Math.min(data.binCount, Math.round((maxFrequencyHz / nyquist) * data.binCount)),
  );

  const image = context.createImageData(data.frameCount, usableBins);

  for (let x = 0; x < data.frameCount; x++) {
    const column = data.magnitudes[x]!;
    for (let y = 0; y < usableBins; y++) {
      // y = 0 is the top of the image and the highest frequency.
      const bin = usableBins - 1 - y;
      const [r, g, b] = ramp(Math.pow(column[bin] ?? 0, 0.85));
      const i = (y * data.frameCount + x) * 4;
      image.data[i] = r;
      image.data[i + 1] = g;
      image.data[i + 2] = b;
      image.data[i + 3] = 255;
    }
  }

  // Render at native resolution, then upscale — spectrograms are binned, and
  // pretending otherwise by interpolating during computation would be a lie
  // about the data.
  const offscreen = document.createElement('canvas');
  offscreen.width = data.frameCount;
  offscreen.height = usableBins;
  offscreen.getContext('2d')?.putImageData(image, 0, 0);

  context.imageSmoothingEnabled = true;
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.drawImage(offscreen, 0, 0, canvas.width, canvas.height);
}
